import axios from "axios";
import Vue from "vue";

// Routes shared by the auth flow. Auth0's logout returnTo needs an absolute
// URL, so callers prepend window.location.origin where required.
export const LOGIN_ROUTE = "/authentication/login";
export const AUTH0_CALLBACK_ROUTE = "/authentication/auth0-callback";
export const AUTH_ROUTE_PREFIX = "/authentication/";

// Returns a catch handler for requests whose failure must never surface to
// the user (the interceptors in main.js already handle session expiry). The
// error is still visible during development so it is not silently lost.
export const swallowError = context => error => {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[PO] Ignored request failure (${context}):`, error);
  }
};

let validationInFlight = null;
let validatedUntil = 0;

export function clearPoSession() {
  [
    "token",
    "tokens",
    "users",
    "isAuthenticated",
    "site_id",
    "active_menu_paths",
  ].forEach(key => localStorage.removeItem(key));
  validationInFlight = null;
  validatedUntil = 0;
}

export function poToken() {
  return localStorage.getItem("token") || "";
}

export function installPoBearerInterceptor() {
  axios.interceptors.request.use(config => {
    const token = poToken();
    let sameOrigin = false;
    try {
      sameOrigin = new URL(config.url || "", window.location.origin).origin
        === window.location.origin;
    } catch (error) {
      sameOrigin = false;
    }
    if (token && sameOrigin) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
}

export async function validatePoSession(force = false) {
  if (!poToken()) return null;
  if (!force && Date.now() < validatedUntil) {
    return { authenticated: true };
  }
  if (validationInFlight) return validationInFlight;

  validationInFlight = axios
    .get(Vue.prototype.$apiurl + "api/web/common/auth-check", { timeout: 30000 })
    .then(response => {
      const payload = response.data && response.data.data
        ? response.data.data
        : response.data;
      if (
        !payload ||
        payload.authenticated !== true ||
        payload.site_id === undefined ||
        payload.site_id === null
      ) {
        throw new Error("The PO session response was not authoritative.");
      }
      validatedUntil = Date.now() + 30000;
      return payload;
    })
    .catch(error => {
      clearPoSession();
      throw error;
    })
    .finally(() => {
      validationInFlight = null;
    });

  return validationInFlight;
}

// Fetches the site's auth configuration. Fails closed: with no authoritative
// answer, assume Auth0-only and not ready so no local login form is offered
// and no Auth0 write operations are attempted.
export async function fetchAuthConfig(apiUrl) {
  try {
    const response = await axios.get(apiUrl + "api/web/site/auth-config");
    const config = response.data.data;
    return {
      auth0Only: config.method === "auth0",
      auth0Ready: config.auth0_ready === true,
    };
  } catch (error) {
    return { auth0Only: true, auth0Ready: false };
  }
}

export function newCorrelationId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  const random = Array.from(bytes)
    .map(value => value.toString(16).padStart(2, "0"))
    .join("");
  return `po:${Date.now().toString(36)}:${random}`;
}

// Shared by the admin user-management views: Auth0-mode detection plus
// per-operation correlation ids. An id is memoised per operation key so a
// retry of the same operation reuses it, letting the backend deduplicate;
// it is cleared once the operation settles server-side.
export const poAuthAdminMixin = {
  data() {
    return {
      auth0Only: true,
      auth0Ready: false,
      operationCorrelations: {},
    };
  },
  methods: {
    correlationId(operation) {
      if (!this.operationCorrelations[operation]) {
        this.$set(this.operationCorrelations, operation, newCorrelationId());
      }
      return this.operationCorrelations[operation];
    },
    clearCorrelationId(operation) {
      this.$delete(this.operationCorrelations, operation);
    },
    async getAuthMode() {
      const config = await fetchAuthConfig(this.$apiurl);
      this.auth0Only = config.auth0Only;
      this.auth0Ready = config.auth0Ready;
    },
  },
};
